/**
 * Commands that change what is written inside a part: its notes, marks, clefs and slurs.
 *
 * Every one of them inverts to a snapshot -- the bar as it was, or the part's spanners as
 * they were. That is deliberate. An intent-shaped inverse ("put the quarter note back")
 * has to reconstruct state the forward command never carried, and gets it subtly wrong the
 * moment the edit also touched something else: deleting a note also deletes the dynamic
 * hanging off it and the slur that ended on it. One bar is a few kilobytes at worst, and a
 * snapshot of it cannot be subtly wrong.
 */

import { locateEvent } from "../document/query";
import type { EventId } from "../document/ids";
import type { MusicEvent, PartMeasure, Score } from "../document/types";
import {
  done,
  fail,
  findPart,
  replacePart,
  replacePartMeasure,
  withVoice,
  withoutEvents,
} from "./helpers";
import type {
  AddDirection,
  AddSpanner,
  Command,
  CommandResult,
  InsertEvent,
  RemoveDirection,
  RemoveEvent,
  RemoveSpanner,
  ReplaceEvent,
  SetClef,
  SetInstrument,
  SetDuration,
  SetEventProps,
  SetPartMeasure,
  SetPitches,
  SetSpanners,
} from "./types";

export function applySetPartMeasure(score: Score, command: SetPartMeasure): CommandResult {
  const part = findPart(score, command.partId);
  const before = part?.measures[command.measureId];
  if (part === null || before === undefined) {
    return fail(command, `no measure ${command.measureId} in part ${command.partId}`);
  }

  const next = replacePartMeasure(score, command.partId, command.measureId, command.measure);
  if (next === null) return fail(command, "measure vanished while applying");
  return done(next, { ...command, measure: before });
}

export function applySetSpanners(score: Score, command: SetSpanners): CommandResult {
  const part = findPart(score, command.partId);
  if (part === null) return fail(command, `no part ${command.partId}`);
  return done(replacePart(score, { ...part, spanners: command.spanners }), {
    kind: "setSpanners",
    partId: command.partId,
    spanners: part.spanners,
  });
}

/** Rebuild one bar of one part, inverting to the bar as it was. */
function editMeasure(
  score: Score,
  command: Command,
  partId: SetPartMeasure["partId"],
  measureId: SetPartMeasure["measureId"],
  change: (measure: PartMeasure) => PartMeasure | string,
): CommandResult {
  const part = findPart(score, partId);
  const before = part?.measures[measureId];
  if (part === null || before === undefined) {
    return fail(command, `no measure ${measureId} in part ${partId}`);
  }

  const changed = change(before);
  if (typeof changed === "string") return fail(command, changed);

  const next = replacePartMeasure(score, partId, measureId, changed);
  if (next === null) return fail(command, "measure vanished while applying");
  return done(next, { kind: "setPartMeasure", partId, measureId, measure: before });
}

/** Rebuild the one event named by id, wherever in the score it turns out to be. */
function editEvent(
  score: Score,
  command: Command,
  eventId: EventId,
  change: (event: MusicEvent) => MusicEvent | string,
): CommandResult {
  const at = locateEvent(score, eventId);
  if (at === null) return fail(command, `no event ${eventId}`);

  return editMeasure(score, command, at.partId, at.measureId, (measure) => {
    const changed = change(at.event);
    if (typeof changed === "string") return changed;
    return (
      withVoice(measure, at.voiceIndex, (voice) => ({
        ...voice,
        events: voice.events.map((event, index) => (index === at.eventIndex ? changed : event)),
      })) ?? "voice vanished while applying"
    );
  });
}

export function applyInsertEvent(score: Score, command: InsertEvent): CommandResult {
  if (locateEvent(score, command.event.id) !== null) {
    return fail(command, `event ${command.event.id} is already in the score`);
  }

  return editMeasure(score, command, command.partId, command.measureId, (measure) => {
    const voice = measure.voices[command.voiceIndex];
    if (voice === undefined) return `no voice ${command.voiceIndex} in that measure`;
    const events = [...voice.events];
    events.splice(Math.max(0, Math.min(command.index, events.length)), 0, command.event);
    return withVoice(measure, command.voiceIndex, (v) => ({ ...v, events })) ?? "voice vanished";
  });
}

/**
 * Deleting an event also deletes what pointed at it, which is why this one alone rebuilds
 * the whole part and inverts to two snapshots rather than one.
 */
export function applyRemoveEvent(score: Score, command: RemoveEvent): CommandResult {
  const at = locateEvent(score, command.eventId);
  if (at === null) return fail(command, `no event ${command.eventId}`);

  const part = findPart(score, at.partId);
  const before = part?.measures[at.measureId];
  if (part === null || before === undefined) return fail(command, "part vanished while applying");

  const inverse: Command = {
    kind: "batch",
    label: "restore event",
    commands: [
      { kind: "setPartMeasure", partId: at.partId, measureId: at.measureId, measure: before },
      { kind: "setSpanners", partId: at.partId, spanners: part.spanners },
    ],
  };

  return done(replacePart(score, withoutEvents(part, new Set([command.eventId]))), inverse);
}

export function applyReplaceEvent(score: Score, command: ReplaceEvent): CommandResult {
  if (command.event.id !== command.eventId) {
    return fail(command, "a replacement keeps the event's id, or its slurs lose an end");
  }
  return editEvent(score, command, command.eventId, () => command.event);
}

export function applySetDuration(score: Score, command: SetDuration): CommandResult {
  return editEvent(score, command, command.eventId, (event) => ({
    ...event,
    duration: command.duration,
  }));
}

export function applySetPitches(score: Score, command: SetPitches): CommandResult {
  return editEvent(score, command, command.eventId, (event) => {
    if (event.kind !== "note") return "a rest has no pitches; replace it with a note first";
    if (command.pitches.length === 0) return "a note event needs at least one pitch";
    return { ...event, pitches: command.pitches };
  });
}

export function applySetEventProps(score: Score, command: SetEventProps): CommandResult {
  return editEvent(score, command, command.eventId, (event) => {
    if (event.kind === "note") return { ...event, ...command.props };
    const { fermata, cue, offset, staff, ...ofNotes } = command.props;
    if (Object.keys(ofNotes).length > 0) {
      return "stems, beams, articulations, ornaments and lyrics belong to notes";
    }
    // A fermata, a cue, a nudge and the staff it is drawn on are what a rest shares with a
    // note, and each is written only when the command mentions it: spreading the field
    // unasked would set it to `undefined` and lose what the rest already said.
    return {
      ...event,
      ...(fermata === undefined ? {} : { fermata }),
      ...(cue === undefined ? {} : { cue }),
      ...(offset === undefined ? {} : { offset }),
      ...(staff === undefined ? {} : { staff }),
    };
  });
}

export function applySetClef(score: Score, command: SetClef): CommandResult {
  const part = findPart(score, command.partId);
  if (part === null) return fail(command, `no part ${command.partId}`);
  if (part.staves[command.staff] === undefined) {
    return fail(command, `part ${command.partId} has no staff ${command.staff}`);
  }

  return editMeasure(score, command, command.partId, command.measureId, (measure) => {
    const clefChanges = { ...measure.clefChanges };
    if (command.clef === null) delete clefChanges[command.staff];
    else clefChanges[command.staff] = command.clef;
    return { ...measure, clefChanges };
  });
}

/**
 * Hand the player another instrument at a bar, or take the change away.
 *
 * Only what is played on is changed: a kit and a tuning belong to the part for the whole
 * piece -- the staff is drawn for them -- and nothing here can move them.
 */
export function applySetInstrument(score: Score, command: SetInstrument): CommandResult {
  if (findPart(score, command.partId) === null) return fail(command, `no part ${command.partId}`);
  return editMeasure(score, command, command.partId, command.measureId, (measure) => ({
    ...measure,
    instrument: command.instrument,
  }));
}

export function applyAddDirection(score: Score, command: AddDirection): CommandResult {
  return editMeasure(score, command, command.partId, command.measureId, (measure) => {
    if (locateEvent(score, command.direction.anchor) === null) {
      return `no event ${command.direction.anchor} to hang that direction on`;
    }
    return { ...measure, directions: [...measure.directions, command.direction] };
  });
}

export function applyRemoveDirection(score: Score, command: RemoveDirection): CommandResult {
  return editMeasure(score, command, command.partId, command.measureId, (measure) => {
    if (!measure.directions.some((direction) => direction.id === command.directionId)) {
      return `no direction ${command.directionId} in that measure`;
    }
    return {
      ...measure,
      directions: measure.directions.filter((d) => d.id !== command.directionId),
    };
  });
}

export function applyAddSpanner(score: Score, command: AddSpanner): CommandResult {
  const part = findPart(score, command.partId);
  if (part === null) return fail(command, `no part ${command.partId}`);
  for (const end of [command.spanner.from, command.spanner.to]) {
    const at = locateEvent(score, end);
    if (at === null) return fail(command, `no event ${end} to attach that spanner to`);
    if (at.partId !== command.partId) return fail(command, "a spanner stays inside one part");
  }

  return done(replacePart(score, { ...part, spanners: [...part.spanners, command.spanner] }), {
    kind: "setSpanners",
    partId: command.partId,
    spanners: part.spanners,
  });
}

export function applyRemoveSpanner(score: Score, command: RemoveSpanner): CommandResult {
  const part = findPart(score, command.partId);
  if (part === null) return fail(command, `no part ${command.partId}`);
  if (!part.spanners.some((spanner) => spanner.id === command.spannerId)) {
    return fail(command, `no spanner ${command.spannerId} in that part`);
  }

  const spanners = part.spanners.filter((spanner) => spanner.id !== command.spannerId);
  return done(replacePart(score, { ...part, spanners }), {
    kind: "setSpanners",
    partId: command.partId,
    spanners: part.spanners,
  });
}
